/*  MUI  */
import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { CustomizedDot } from 'application/Shared/Components/Reports/CustomizedDot';
import { FontSizes } from 'application/Shared/Constants';
import { getScoreBorderColor } from 'application/Shared/Reports/ScoreBorderColor';
import { getScoreFillColor } from 'application/Shared/Reports/ScoreFillColor';

/*  React  */
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

/*  Components  */
import { QBRScoreChip } from './QBRScoreChip';

/*  Constants  */
const reportPrefix = 'qbrQBRFirst';
const today = new Date();
const todayFormatted = today.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
});

const scoreGrade = (value) => {
    if (value < 25) {
        return 'Poor';
    }
    if (value < 40) {
        return 'Okay';
    }
    if (value < 70) {
        return 'Great';
    }

    return 'Excellent';
};

const ScoreChipInlined = ({ score }) => (
    <QBRScoreChip score={score} renderType="inline" />
);

const getPointChangeVerbiage = ({ pointChange, pointDirection, t }) => {
    if (pointChange === 0) {
        return t('QBR.yourScorehasStayedTheSame');
    }
    if (pointDirection === '-') {
        return t('QBR.yourScorehasDecreased', {
            count: pointChange,
            pointChange
        });
    }
    return t('QBR.yourScorehasIncreased', { count: pointChange, pointChange });
};

export const QBRScoreHistoryAndInsights = ({ reportType, props }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { reportData, scoreMap } = props;
    const { dayDifference, lastDay, pointChange, history } = reportData;
    const reportKey = `${reportPrefix}${reportType}`;
    const score = scoreMap[reportKey];
    let { pointDirection } = reportData;
    let percentChangeColor =
        theme.palette.qbrFirstReports.scoreTextColor.increase;

    if (pointChange === 0) {
        percentChangeColor =
            theme.palette.qbrFirstReports.scoreTextColor.unchanged;
        pointDirection = '';
    }

    if (pointDirection === '-') {
        percentChangeColor =
            theme.palette.qbrFirstReports.scoreTextColor.decrease;
    }

    // If it is a quarter, the last day will be 90 days after the firstDay
    // However, we only have data until 'today', if it is the current quarter.
    // If it is a period, or a different quarter, the endDate will be before or equal to today.
    // Therefore, the endDate will be whichever is earlier.
    const endDate =
        new Date(todayFormatted) < new Date(lastDay) ? todayFormatted : lastDay;

    return (
        <Grid
            container
            width="100%"
            data-trackid="renderScoreHistoryAndInsights"
        >
            <Grid
                item
                xs={6}
                style={{ padding: '8px' }}
                data-trackid="qbrRenderScoreHistoryAndInsightsChart"
            >
                <Typography variant="h5" style={{ marginBottom: '8px' }}>
                    {t('QBR.scoreHistoryNDays', { dayDifference })}
                </Typography>
                <ResponsiveContainer height={244} width="100%">
                    <LineChart
                        height={244}
                        width={400}
                        data={history}
                        margin={{
                            bottom: 20,
                            left: 0,
                            right: 30,
                            top: 32
                        }}
                    >
                        <CartesianGrid
                            stroke={theme.palette.chart.axis}
                            strokeDasharray="3 3"
                        />
                        <XAxis dataKey="day" angle={45} textAnchor="start" />
                        <YAxis />
                        <Tooltip />
                        <Line
                            type="linear"
                            dataKey="score"
                            stroke={theme.palette.qbrFirstReports[reportType]}
                            strokeWidth={4}
                            dot={
                                <CustomizedDot
                                    circleWidth="13"
                                    dynamicBorderColorsFn={(value) =>
                                        getScoreBorderColor({ theme, value })
                                    }
                                    dynamicColorsFn={(value) =>
                                        getScoreFillColor({ theme, value })
                                    }
                                    fontSize={FontSizes[0]}
                                    showText
                                />
                            }
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Grid>
            <Grid
                item
                xs={6}
                style={{ padding: '8px' }}
                data-trackid="qbrRenderScoreHistoryAndInsightsList"
            >
                <Typography variant="h5" style={{ marginBottom: '8px' }}>
                    {t('QBR.scoreInsightsNDays', { dayDifference })}
                </Typography>
                <ul>
                    <li>
                        <Trans
                            i18nKey="QBR.yourCurrentScore.whichIsConsidered"
                            components={{
                                MakeItBold: (
                                    <Typography variant="span" fontWeight={600}>
                                        {scoreGrade(score)}
                                    </Typography>
                                ),
                                ScoreChip: <ScoreChipInlined score={score} />
                            }}
                        />
                    </li>
                    <li>
                        {`${t('QBR.betweenFirstAndLastDays', {
                            firstDay: reportData.firstDay,
                            lastDay: endDate
                        })}:`}
                        <ul>
                            <li>
                                {getPointChangeVerbiage({
                                    pointChange,
                                    pointDirection,
                                    t
                                })}
                            </li>
                            <li>
                                <Typography
                                    variant="span"
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'QBR.changeOvertheLastNDaysPercent',
                                            {
                                                dayDifference:
                                                    reportData.dayDifference,
                                                percent:
                                                    reportData.pointChangePercent,
                                                percentChangeColor,
                                                pointDirection
                                            }
                                        )
                                    }}
                                />
                            </li>
                        </ul>
                    </li>
                    <li>
                        <Trans
                            i18nKey="QBR.yourAchievedYourHighestScoreOn"
                            values={{
                                day: reportData.highestScore.day
                            }}
                            components={{
                                ScoreChip: (
                                    <ScoreChipInlined
                                        score={reportData.highestScore.score}
                                    />
                                )
                            }}
                        />
                    </li>
                </ul>
            </Grid>
            <hr />
        </Grid>
    );
};
